import Worker from './engine-warpper.worker.js'
import { script } from '@/../node_modules/dynamic-import/dist/import.js'
import { threads, simd, relaxedSimd } from 'wasm-feature-detect'

var callback, engineInstance, supportThreads, dataLoaded

// Path on your server where rapfi.db and version.json are served.
// version.json should be: { "version": "some-string-that-changes-when-db-updates" }
const DB_URL = '/database/rapfi.db'
const DB_VERSION_URL = '/database/version.json'
const IDB_DB_NAME = 'gomocalc-rapfi'
const IDB_STORE_NAME = 'files'
const IDB_KEY = 'rapfi.db'
const IDB_VERSION_KEY = 'rapfi.db.version'
const WASM_DB_PATH = '/rapfi.db'

// ─── IndexedDB helpers ────────────────────────────────────────────────────────

function openIDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_DB_NAME, 1)
    req.onupgradeneeded = (e) => {
      e.target.result.createObjectStore(IDB_STORE_NAME)
    }
    req.onsuccess = (e) => resolve(e.target.result)
    req.onerror = (e) => reject(e.target.error)
  })
}

function idbGet(db, key) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE_NAME, 'readonly')
    const req = tx.objectStore(IDB_STORE_NAME).get(key)
    req.onsuccess = (e) => resolve(e.target.result)
    req.onerror = (e) => reject(e.target.error)
  })
}

function idbPut(db, key, value) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE_NAME, 'readwrite')
    const req = tx.objectStore(IDB_STORE_NAME).put(value, key)
    req.onsuccess = () => resolve()
    req.onerror = (e) => reject(e.target.error)
  })
}

// ─── DB loading ───────────────────────────────────────────────────────────────

// Fetch the current version tag from the server (tiny JSON, always fresh).
async function fetchServerVersion() {
  try {
    const res = await fetch(DB_VERSION_URL, { cache: 'no-store' })
    if (!res.ok) return null
    const { version } = await res.json()
    return version || null
  } catch {
    return null
  }
}

// Download rapfi.db with progress updates via callback({ dbProgress: 0..1 }).
async function fetchDatabase() {
  const res = await fetch(DB_URL)
  if (!res.ok) throw new Error(`Failed to fetch database: ${res.status}`)

  const contentLength = res.headers.get('Content-Length')
  const total = contentLength ? parseInt(contentLength, 10) : null
  const reader = res.body.getReader()
  const chunks = []
  let loaded = 0

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    chunks.push(value)
    loaded += value.length
    if (total) callback({ dbProgress: loaded / total })
  }

  // Concatenate into a single Uint8Array
  const bytes = new Uint8Array(loaded)
  let offset = 0
  for (const chunk of chunks) {
    bytes.set(chunk, offset)
    offset += chunk.length
  }
  return bytes
}

// Load the database bytes into the WASM filesystem and set it as the active DB.
// Emscripten exports FS_createDataFile/FS_unlink rather than FS.writeFile.
function loadDatabaseIntoWasm(bytes) {
  if (supportThreads) {
    // Delete existing file first (ignore error if not found), then create
    try { engineInstance.FS_unlink(WASM_DB_PATH) } catch (_) {}
    engineInstance.FS_createDataFile('/', 'rapfi.db', bytes, true, true, true)
    engineInstance.sendCommand('YXSETDATABASE ' + WASM_DB_PATH)
    callback({ dbReady: true })
  } else {
    // Single-threaded: transfer the buffer to the worker (zero-copy)
    engineInstance.postMessage(
      { type: 'writeFile', path: WASM_DB_PATH, data: bytes.buffer },
      [bytes.buffer]
    )
  }
}

// Main entry point: check cache, download if needed, write to WASM FS.
// Notifies via callback({ dbProgress }) during download and
// callback({ dbReady }) when the DB has been sent to YXSETDATABASE.
async function setupDatabase() {
  let db
  try {
    db = await openIDB()
  } catch (e) {
    console.warn('IndexedDB unavailable, skipping database cache:', e)
    callback({ dbReady: false })
    return
  }

  const serverVersion = await fetchServerVersion()
  const cachedVersion = await idbGet(db, IDB_VERSION_KEY)
  const cachedBytes = await idbGet(db, IDB_KEY)

  let bytes
  if (cachedBytes && cachedVersion && cachedVersion === serverVersion) {
    // Cache hit — use stored bytes directly, no download needed
    callback({ dbProgress: 1.0 })
    bytes = cachedBytes
  } else {
    // Cache miss or stale — download and store
    callback({ dbProgress: 0 })
    try {
      bytes = await fetchDatabase()
      await idbPut(db, IDB_KEY, bytes)
      if (serverVersion) await idbPut(db, IDB_VERSION_KEY, serverVersion)
    } catch (e) {
      console.error('Database download failed:', e)
      callback({ dbReady: false })
      return
    }
  }

  loadDatabaseIntoWasm(bytes)

  // For the single-threaded path the worker sends YXSETDATABASE itself
  // after writing the file (see engine-warpper.worker.js), and posts
  // { type: 'dbReady' } back — handled in onEngineMessage below.
  // For multi-threaded, loadDatabaseIntoWasm handles sendCommand + dbReady.
}

// ─── Engine init ──────────────────────────────────────────────────────────────

function locateFile(url, engineDirURL) {
  // Redirect 'rapfi.*\.data' to 'rapfi.data'
  if (/^rapfi.*\.data$/.test(url)) url = 'rapfi.data'
  return engineDirURL + url
}

function getWasmMemoryArguments(isShared, maximum_memory_mb = 2048) {
  return {
    initial: 64 * ((1024 * 1024) / 65536), // 64MB
    maximum: maximum_memory_mb * ((1024 * 1024) / 65536),
    shared: isShared,
  }
}

function instantiateSharedWasmMemory() {
  let maximum_memory_mb = 2048
  while (maximum_memory_mb > 512) {
    try {
      const memory = new WebAssembly.Memory(getWasmMemoryArguments(true, maximum_memory_mb))
      memory.grow(1)
      return memory
    } catch (e) {
      maximum_memory_mb /= 2
    }
  }
  return new WebAssembly.Memory(getWasmMemoryArguments(true, maximum_memory_mb))
}

// Init engine and setup callback function for receiving engine output.
// After the engine signals ready, the DB is loaded and YXSETDATABASE is sent.
async function init(callbackFn_, loadFullEngine) {
  callback = callbackFn_
  dataLoaded = false

  supportThreads = await threads()
  const supportSIMD = await simd()
  const supportRelaxedSIMD = supportThreads && (await relaxedSimd())

  const engineFlags =
    (supportThreads ? '-multi' : '-single') +
    (supportSIMD && loadFullEngine ? '-simd128' : '') +
    (supportRelaxedSIMD && loadFullEngine ? '-relaxed' : '')
  const engineDir = loadFullEngine ? '/' : '/fallback/'
  const engineURL = `${process.env.BASE_URL}build${engineDir}rapfi${engineFlags}.js`

  if (supportThreads) {
    await script.import(/* webpackIgnore: true */ engineURL)

    const engineDirURL = engineURL.substring(0, engineURL.lastIndexOf('/') + 1)

    engineInstance = await self['Rapfi']({
      locateFile: (url) => locateFile(url, engineDirURL),
      onReceiveStdout: (o) => onEngineStdout(o),
      onReceiveStderr: (o) => onEngineStderr(o),
      onExit: (c) => onEngineExit(c),
      setStatus: (s) => onEngineStatus(s),
      wasmMemory: instantiateSharedWasmMemory(),
    })
    dataLoaded = true
    // Load DB into WASM FS, then signal ready to the store
    await setupDatabase()
    callback({ ok: true })
  } else {
    engineInstance = new Worker()

    engineInstance.onmessage = (e) => {
      const { type, data } = e.data
      if (type === 'stdout') onEngineStdout(data)
      else if (type === 'stderr') onEngineStderr(data)
      else if (type === 'exit') onEngineExit(data)
      else if (type === 'status') onEngineStatus(data)
      else if (type === 'ready') {
        dataLoaded = true
        // Worker is up — now load the DB (async, worker handles writeFile + YXSETDATABASE)
        setupDatabase()
      } else if (type === 'dbReady') {
        // Worker has written the file and sent YXSETDATABASE
        callback({ dbReady: data })
        callback({ ok: true })
      } else {
        console.error('received unknown message from worker: ', e.data)
      }
    }

    engineInstance.onerror = (err) => {
      console.error('worker error: ' + err.message + '\nretrying after 0.5s...')
      engineInstance.terminate()
      setTimeout(() => init(callback, loadFullEngine), 500)
    }

    engineInstance.postMessage({
      type: 'engineScriptURL',
      data: {
        engineURL: engineURL,
        memoryArgs: getWasmMemoryArguments(false),
      },
    })
  }

  return engineURL
}

// Stop current engine's thinking process
// Returns true if force stopped, otherwise returns false
function stopThinking() {
  if (supportThreads) {
    sendCommand('YXSTOP')
    return false
  } else {
    engineInstance.terminate()
    init(callback)
    return true
  }
}

// Send a command to engine
function sendCommand(cmd) {
  if (typeof cmd !== 'string' || cmd.length == 0) return

  if (supportThreads) engineInstance.sendCommand(cmd)
  else engineInstance.postMessage({ type: 'command', data: cmd })
}

// Process output from engine and call callback function
function onEngineStdout(output) {
  let i = output.indexOf(' ')

  if (i == -1) {
    if (output == 'OK') return
    else if (output == 'SWAP') callback({ swap: true })
    else {
      const coord = output.split(',')
      callback({ pos: [+coord[0], +coord[1]] })
    }
    return
  }

  let head = output.substring(0, i)
  let tail = output.substring(i + 1)

  if (head == 'MESSAGE') {
    if (tail.startsWith('REALTIME')) {
      let r = tail.split(' ')
      if (r.length < 3) {
        callback({
          realtime: {
            type: r[1],
          },
        })
      } else {
        let coord = r[2].split(',')
        callback({
          realtime: {
            type: r[1],
            pos: [+coord[0], +coord[1]],
          },
        })
      }
    } else {
      callback({ msg: tail })
    }
  } else if (head == 'INFO') {
    i = tail.indexOf(' ')
    head = tail.substring(0, i)
    tail = tail.substring(i + 1)

    if (head == 'PV') callback({ multipv: tail })
    else if (head == 'NUMPV') callback({ numpv: +tail })
    else if (head == 'DEPTH') callback({ depth: +tail })
    else if (head == 'SELDEPTH') callback({ seldepth: +tail })
    else if (head == 'NODES') callback({ nodes: +tail })
    else if (head == 'TOTALNODES') callback({ totalnodes: +tail })
    else if (head == 'TOTALTIME') callback({ totaltime: +tail })
    else if (head == 'SPEED') callback({ speed: +tail })
    else if (head == 'EVAL') callback({ eval: tail })
    else if (head == 'WINRATE') callback({ winrate: parseFloat(tail) })
    else if (head == 'BESTLINE')
      callback({ bestline: tail.match(/\d+,\d+/g).map((s) => s.split(',').map(Number)) })
  } else if (head == 'FORBID') {
    callback({
      forbid: (tail.match(/.{4}/g) || []).map((s) => {
        let coord = s.match(/([0-9][0-9])([0-9][0-9])/)
        let x = +coord[1]
        let y = +coord[2]
        return [x, y]
      }),
    })
  } else if (head == 'DATABASE') {
    if (tail == 'DONE') {
      callback({ dbDone: true })
    } else if (tail.startsWith('TEXT ')) {
      // DATABASE TEXT "comment text" — position-level comment
      const match = tail.match(/^TEXT "(.*)"$/)
      callback({ dbText: match ? match[1] : '' })
    } else if (tail.startsWith('REFRESH')) {
      callback({ dbRefresh: true })
    } else {
      // DATABASE <x> <y> <labelVal> <value> <depth> <bound> <hasComment> [boardText...]
      const parts = tail.split(' ')
      if (parts.length >= 7) {
        const x = +parts[0]
        const y = +parts[1]
        const labelVal = +parts[2]  // -1 = no label, else int32-encoded ASCII string
        const value = +parts[3]
        const depth = +parts[4]
        const bound = +parts[5]
        const hasComment = parts[6] === '1'
        // boardText is the rest of the line after the 7 fixed fields (may be empty)
        const boardText = parts.length > 7 ? parts.slice(7).join(' ') : ''

        // Decode labelVal: int32 encoding of up to 4 ASCII chars, packed big-endian
        let displayLabel = ''
        if (labelVal > 0) {
          for (let shift = 24; shift >= 0; shift -= 8) {
            const ch = (labelVal >> shift) & 0xff
            if (ch !== 0) displayLabel += String.fromCharCode(ch)
          }
        }

        callback({
          dbEntry: { pos: [x, y], displayLabel, value, depth, bound, hasComment, boardText },
        })
      }
    }
  } else if (head == 'ERROR') {
    callback({ error: tail })
  } else if (head.indexOf(',') != -1) {
    const coord1 = head.split(',')
    const coord2 = tail.split(',')
    callback({
      pos: [+coord1[0], +coord1[1]],
      pos2: [+coord2[0], +coord2[1]],
    })
  } else {
    callback({ unknown: tail })
  }
}

function onEngineStderr(output) {
  console.error('[Engine Error] ' + output)
}

function onEngineExit(code) {
  console.log('[Engine Exit] ' + code)
}

function onEngineStatus(status) {
  if (dataLoaded) return

  if (status === 'Running...' || status === '') {
    dataLoaded = true
    callback({
      loading: {
        progress: 1.0,
      },
    })
  }

  const match = status.match(/\((\d+)\/(\d+)\)/)
  if (match) {
    const loadedBytes = parseInt(match[1], 10)
    const totalBytes = parseInt(match[2], 10)
    if (loadedBytes == totalBytes) dataLoaded = true
    callback({
      loading: {
        progress: loadedBytes / totalBytes,
        loadedBytes: loadedBytes,
        totalBytes: totalBytes,
      },
    })
  }
}

export { init, sendCommand, stopThinking }
